// React
import { useMemo } from "react"

// UI
import { Heading, P } from "@/components/Typography"
import { Text } from "@visx/text"
import {
	GraphData,
	MultiLineGraph,
	TooltipComponentProps,
	TooltipComponentType,
} from "@/components/graphs/types/multi-line/MultiLineGraph"

// Helpers
import { emptyValue } from "@/utils/helpers"

// Hooks
import { useCurrentFinanceProjectId } from "../hooks/useCurrentFinanceProjectId"

// Translation
import { useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"

// REST queries
import { useApiV1ComparisonProjectEndexProgressRetrieve } from "@/api/rest/generated/api/api"
import { EndexTariffItem } from "@/api/rest/generated/@types"

/**
 * FinanceGraphSolarInterestTariff
 * @returns
 */
export function FinanceGraphSolarInterestTariff() {
	const { formatCurrency } = useLang()

	// Rest Query
	const projectId = useCurrentFinanceProjectId() ?? 1 // TODO: Pick a default
	const { data } = useApiV1ComparisonProjectEndexProgressRetrieve(projectId)

	const graphData = useMemo(
		() =>
			data?.endex_tariffs
				?.filter(Boolean)
				.map((entry: EndexTariffItem) => {
					return {
						x: entry.date,
						y: Math.max(entry.tariff ?? 0, 0), // Should never go below zero
					} as GraphData
				}) ?? [],
		[data?.endex_tariffs],
	)

	const expectedData = useMemo(
		() =>
			data?.average_tariffs
				?.filter(Boolean)
				.map((entry: EndexTariffItem) => {
					return {
						x: entry.date,
						y: entry.tariff ?? 0, // // Should never go below zero
					} as GraphData
				}) ?? [],
		[data?.average_tariffs],
	)

	const lineDatas = useMemo(
		() => [
			{
				data: graphData,
				id: "performance_ratio",
				variant: "primary" as const,
			},
			{
				data: expectedData,
				id: "expected_performance_ratio",
			},
		],
		[graphData, expectedData],
	)

	if (graphData.length === 0) {
		return <GraphProductionNoData />
	}

	return (
		<MultiLineGraph
			lineDatas={lineDatas}
			margin={{ left: 66, right: 25, top: 25, bottom: 50 }}
			TooltipComponent={EndexTariffTooltip}
			xTickComponent={({ formattedValue, ...props }) => {
				return (
					// oxlint-disable-next-line react/prop-types
					<Text {...props} x={props.x + 5}>
						{formattedValue}
					</Text>
				)
			}}
			yTickComponent={({ formattedValue, ...props }) => (
				<Text {...props}>
					{formattedValue
						? formatCurrency(Number(formattedValue))
						: emptyValue()}
				</Text>
			)}
		/>
	)
}

/**
 * EndexTariffTooltip
 * @param param0
 * @returns
 */
const EndexTariffTooltip: TooltipComponentType = ({
	label,
	value,
	value2,
}: TooltipComponentProps) => {
	const t = useTrans("finance")
	const { formatCurrency } = useLang()

	function getValue(value: number | string) {
		return formatCurrency(Number(value))
	}

	// only add decimal points if necessary
	if (typeof value === "number" && Math.round(value) !== value) {
		value = value.toFixed(2)
	}
	if (typeof value2 === "number" && Math.round(value2) !== value2) {
		value2 = value2.toFixed(2)
	}

	return (
		<div className="px-4 py-3">
			<Heading as="h3" styleAs="h5" className="text-black">
				{label}
			</Heading>
			<P className="grid grid-cols-[10px_auto_1fr] gap-x-3 gap-y-1 text-black md:mt-1">
				{/** endex tariff */}
				{value !== null && (
					<>
						<svg width={10} height={10} viewBox="0 0 10 10">
							<circle
								r={5}
								cx={5}
								cy={5}
								fill="rgb(20, 154, 229)"
							/>
						</svg>
						{t("finance:solar_interest_tariff.block.endex_tariffs")}
						<span className="font-bold">
							{t(
								"finance:solar_interest_tariff.graph.tooltip.endex_tariffs.value",
								{
									year: 24,
									price: getValue(value),
								},
							)}
						</span>
					</>
				)}

				{/** avg endex tariff */}
				{value2 !== null && value !== undefined && (
					<>
						<svg width={10} height={10} viewBox="0 0 10 10">
							<circle r={5} cx={5} cy={5} fill="#4e5155" />
						</svg>
						{t(
							"finance:solar_interest_tariff.block.average_tariffs",
						)}
						<span className="font-bold">
							{t(
								"finance:solar_interest_tariff.graph.tooltip.average_tariffs.value",
								{ price: getValue(value2 as number) },
							)}
						</span>
					</>
				)}
			</P>
		</div>
	)
}

function GraphProductionNoData() {
	const t = useTrans("project")

	return (
		<div className="flex h-full flex-col items-center justify-center">
			<Heading as="h2" styleAs="h4">
				{t("project:data.production.no_data.title")}
			</Heading>
			<p className="mt-2 max-w-lg text-center text-sm text-gray-500">
				{t("project:data.production.no_data.copy")}
			</p>
		</div>
	)
}
